/**
 * External Imports
 */
import { FC, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuAvatar } from '@neutron/react';

/**
 * Internal Imports
 */
import Question from './question';
import Loading from '../../shared/Loading';
import { setCurrentRound } from '../../../redux/actions';
import { useGetQuestionsQuery } from '../../../redux/apis/Templates.api';
import { createAnyId, date } from '../../../utils/helpers';
import { useDebounceValue } from '../../../utils/debouncers';
import supportRoundingTypes from '../../../constants/supportRoundingTypes.json';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Employee,
  ICondensedMasterTemplate,
  Patient,
  Question as IQuestion
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { SurveyContainer } from './Survey.styles';

const { v4: uuidv4 } = require('uuid');

interface ISurveyProps {
  chosenSurvey: any;
  isNew: boolean;
  isViewOnly: boolean;
  loading: boolean;
  round: any;
  selectedPatient?: Patient | null;
  selectedEmployee?: Employee | null;
  selectedTemplate?: ICondensedMasterTemplate;
  selectedTimestamp: string;
  type: string;
  user: AuthUserProps;
}

const Survey: FC<ISurveyProps> = ({
  chosenSurvey,
  isNew,
  isViewOnly,
  loading,
  round,
  selectedPatient,
  selectedEmployee,
  selectedTemplate,
  selectedTimestamp,
  type,
  user
}) => {
  const dispatch = useDispatch();

  const [data, setData] = useState<any>({});

  const debouncedData = useDebounceValue(data, 400);

  const { state } = useLocation();

  const { from = '' } = state ?? {};

  const {
    data: questionsData
    // isError: isQuestionsError,
    // isFetching: isQuestionsFetching,
    // isLoading: isQuestionsLoading
  } = useGetQuestionsQuery(undefined, {
    refetchOnMountOrArgChange: 300,
    skip: !isViewOnly
  });

  const updateAggregatePart = useCallback(
    (aggPart: any, questionPartId: string, newAnswer: any, questions: any) => {
      const { aggregationValue = '' } = aggPart.config;
      if (!aggregationValue) {
        return aggPart;
      }
      const { defaultValue = '' } = aggPart.config;
      const newOptions = [];
      const validValues: string[] = [];
      questions.forEach((q: any) => {
        q.questionParts.forEach((part: any) => {
          if (part.questionType === 'single') {
            let newPartValue;
            let optionId;
            if (part.questionPartId === questionPartId) {
              if (newAnswer.value === aggregationValue) {
                newPartValue = part.question;
                optionId = newAnswer?.optionId ?? uuidv4();
                validValues.push(part.question);
              }
            } else if (
              part.selectedAnswer !== undefined &&
              part.selectedAnswer.length > 0 &&
              part.selectedAnswer[0].value === aggregationValue
            ) {
              newPartValue = part.question;
              optionId = part.selectedAnswer?.optionId ?? uuidv4();
              validValues.push(part.question);
            }
            if (newPartValue !== undefined) {
              newOptions.push({
                optionId,
                value: newPartValue,
                title: newPartValue,
                charge: null,
                flag: null
              });
            }
          }
        });
      });
      if (newOptions.length === 0) {
        newOptions.push({
          optionId: uuidv4(),
          value: defaultValue,
          title: defaultValue,
          charge: null,
          flag: null
        });
        validValues.push(defaultValue);
      }

      const filteredAnswers =
        aggPart.selectedAnswer === undefined
          ? []
          : aggPart.selectedAnswer.filter((answerOption: any) => {
              return validValues.includes(answerOption.value);
            });

      return {
        ...aggPart,
        options: newOptions,
        selectedAnswer: filteredAnswers
      };
    },
    []
  );

  useLayoutEffect(() => {
    if (isNew) {
      if (type === 'patient' && selectedPatient) {
        dispatch(
          setCurrentRound({
            accountNum: selectedPatient.accountNum,
            bed: selectedPatient.bed,
            dept: user.dept,
            division: user.division,
            facilityId: user.facilityId,
            origRoundDate: date.convertDateToUTCTimestamp(new Date()),
            roundDate: selectedTimestamp,
            patientFirstName: selectedPatient.firstName,
            patientId: selectedPatient.urn,
            patientLastName: selectedPatient.lastName,
            patientMRN: selectedPatient.mrn,
            room: selectedPatient.room,
            roundId: createAnyId('round', user.facilityId),
            roundingType: 'patient',
            taskIds: [],
            templateId:
              chosenSurvey?.templateId ?? selectedTemplate?.templateId,
            templateConfigId:
              chosenSurvey?.templateConfigId ??
              selectedTemplate?.templateConfigId,
            templateName: chosenSurvey?.name ?? selectedTemplate?.name,
            title: user.title,
            unit: '', // need to add the unit name here
            unitId: selectedPatient.unitId,
            userFirstName: user.firstName,
            userId: user.hcaid,
            userLastName: user.lastName,
            answers: [],
            _source: 'web'
          })
        );
      }
      if (
        (type === 'employee' ||
          supportRoundingTypes.validation.includes(type) ||
          supportRoundingTypes.support.includes(type)) &&
        selectedEmployee
      ) {
        let roundingMode;
        // TODO: What happens from reports link - how to source the mode from reports?
        switch (from) {
          case '/employees/list/user':
            roundingMode = 'direct';
            break;
          case '/employees/list/delegate':
            roundingMode = 'delegate';
            break;
          case '/employees/list/adhoc':
            roundingMode = 'adhoc';
            break;
          // TODO: Need to handle the case when the user comes from secondary employee lists or tasks
          default:
            break;
        }
        const roundObj: any = {
          dept: user.dept,
          division: selectedEmployee?.division ?? '',
          employeeDept: selectedEmployee?.dept ?? '',
          employeeFirstName: selectedEmployee?.firstName ?? '',
          employeeId: selectedEmployee?.hcaid ?? '',
          employeeLastName: selectedEmployee?.lastName ?? '',
          employeeTitle: selectedEmployee?.title ?? '',
          facilityId: user.facilityId,
          origRoundDate: date.convertDateToUTCTimestamp(new Date()),
          roundDate: selectedTimestamp,
          roundId: createAnyId('round', user.facilityId),
          roundingType: type === 'csc' ? 'csrn' : type,
          taskIds: [],
          templateId: selectedTemplate?.templateId,
          templateConfigId: selectedTemplate?.templateConfigId,
          templateName: selectedTemplate?.name,
          title: user.title,
          userFirstName: user.firstName,
          userId: user.hcaid,
          userLastName: user.lastName,
          _source: 'web',
          answers: []
        };
        if (type === 'employee') {
          roundObj.roundingMode = roundingMode;
        }
        dispatch(setCurrentRound(roundObj));
      }
    }
  }, [chosenSurvey]);

  useLayoutEffect(() => {
    setData({});

    if (chosenSurvey && Object.keys(chosenSurvey).length > 0) {
      if (isNew) {
        setData({
          ...chosenSurvey,
          selectedConditions: [],
          questions: chosenSurvey.questions.map((ques: any) => ({
            ...ques,
            conditions: []
          }))
        });
      } else {
        const questionsWithAggregates: string[] = [];
        const initialData = {
          ...chosenSurvey,
          selectedConditions:
            round.answers?.reduce(
              (conds: string[], ans: any) =>
                ans.answer?.map((a: any) => a.flag).filter((f: string) => f)
                  .length > 0
                  ? [
                      ...conds,
                      {
                        questionId: ans.questionId,
                        flags:
                          ans.answer
                            ?.map((a: any) => a.flag)
                            .filter((f: string) => f) || []
                      }
                    ]
                  : [...conds],
              []
            ) || [],
          // checking if round is still in editable state or view only
          questions: !isViewOnly
            ? chosenSurvey.questions.map((ques: any) => {
                const conditions: any = [];
                return {
                  ...ques,
                  // we go through each question part of the question
                  questionParts: ques.questionParts.map((part: any) => {
                    if (part.questionType === 'aggregate') {
                      questionsWithAggregates.push(ques.questionId);
                    }
                    // if an answer is found for that part store it
                    const foundAnswerForPart = round.answers.find(
                      (ans: any) =>
                        ans.questionId === ques.questionId &&
                        ans.questionPartId === part.questionPartId
                    );
                    // check if its a real answer or unanswered part
                    const hasRealAnswer =
                      foundAnswerForPart?.answer?.length > 0;
                    if (hasRealAnswer) {
                      // add up all the conditions based on the flags for each answer
                      conditions.push(
                        foundAnswerForPart.answer.reduce(
                          (acc: any[], ans: any) =>
                            ans.flag
                              ? [
                                  ...acc,
                                  {
                                    questionPartId: part.questionPartId,
                                    flag: ans.flag
                                  }
                                ]
                              : acc,
                          []
                        ) || []
                      );
                    }
                    // push the answer onto the question part
                    return hasRealAnswer
                      ? {
                          ...part,
                          selectedAnswer: [...foundAnswerForPart.answer]
                        }
                      : { ...part };
                  }),
                  // flatten all the flags from each part into a list on the question
                  conditions: conditions.flat()
                };
              })
            : (questionsData ?? []) // filter out questions that are not in the question bank anymore
                .filter((q: IQuestion) =>
                  round.answers.some(
                    (ans: any) => q.questionId === ans.questionId
                  )
                )
                .map((q: IQuestion) => {
                  const conditions: any = [];
                  return {
                    ...q,
                    // looping through each question part of the question
                    questionParts: q.questionParts.map((part: any) => {
                      // if an answer is found for that part store it
                      const foundAnswerForPart = round.answers.find(
                        (ans: any) =>
                          ans.questionId === q.questionId &&
                          ans.questionPartId === part.questionPartId
                      );
                      // check if its a real answer or unanswered part
                      const hasRealAnswer =
                        foundAnswerForPart?.answer?.length > 0;
                      if (hasRealAnswer) {
                        // add up all the conditions based on the flags for each answer
                        conditions.push(
                          foundAnswerForPart.answer.reduce(
                            (acc: any[], ans: any) =>
                              ans.flag
                                ? [
                                    ...acc,
                                    {
                                      questionPartId: part.questionPartId,
                                      flag: ans.flag
                                    }
                                  ]
                                : acc,
                            []
                          )
                        );
                      }
                      // push the answer onto the question part
                      return hasRealAnswer
                        ? {
                            ...part,
                            selectedAnswer: [...foundAnswerForPart.answer]
                          }
                        : { ...part };
                    }),
                    // flatten all the flags from each part into a list on the question
                    conditions: conditions.flat()
                  };
                })
        };

        if (questionsWithAggregates.length === 0) {
          setData(initialData);
        } else {
          const newQuestions = initialData.questions.map((ques: any) => {
            return !questionsWithAggregates.includes(ques.questionId)
              ? ques
              : {
                  ...ques,
                  questionParts: [
                    ...ques.questionParts.map((part: any) => {
                      return part.questionType !== 'aggregate'
                        ? part
                        : updateAggregatePart(
                            part,
                            'N/A',
                            '',
                            initialData.questions
                          );
                    })
                  ]
                };
          });

          setData({
            ...initialData,
            questions: newQuestions
          });
        }
      }
    }
  }, [chosenSurvey, questionsData]);

  useEffect(() => {
    if (
      !isViewOnly &&
      round?.roundDate &&
      round.roundDate !== selectedTimestamp
    ) {
      dispatch(
        setCurrentRound({
          ...round,
          roundDate: selectedTimestamp
        })
      );
    }
  }, [selectedTimestamp]);

  useEffect(() => {
    if (Object.keys(debouncedData).length > 0) {
      dispatch(
        setCurrentRound({
          ...round,
          answers: isViewOnly
            ? round.answers || []
            : debouncedData.questions.reduce((acc: any[], q: any) => {
                return acc.concat(
                  q.questionParts.map((questionPart: any) => ({
                    question: questionPart.question,
                    questionId: q.questionId,
                    questionPartId: questionPart.questionPartId,
                    answer:
                      questionPart.selectedAnswer &&
                      questionPart.selectedAnswer.length > 0
                        ? questionPart.selectedAnswer.length > 1 ||
                          (questionPart.selectedAnswer.length === 1 &&
                            questionPart.selectedAnswer[0].value.trim())
                          ? questionPart.selectedAnswer
                          : []
                        : []
                  }))
                );
              }, []),

          completed:
            type === 'patient'
              ? debouncedData?.selectedConditions?.some(
                  (cond: { questionId: string; flags: string[] }) =>
                    cond.flags.indexOf('Able Round') !== -1
                )
              : true
        })
      );
    }
  }, [debouncedData]);

  return (
    <SurveyContainer id="Round-Survey" className="w-100" isLoading={loading}>
      {loading && <Loading label="survey" />}
      {!loading &&
        data.questions?.length > 0 &&
        data.questions
          // .filter(
          //   (q: any) =>
          //     q.questionCondition.some((condition: string) =>
          //       data.selectedConditions.some(
          //         (c: { questionId: string; flags: string[] }) =>
          //           c.flags.indexOf(condition) !== -1
          //       )
          //     ) || !q.questionCondition.length
          // ) // Commented for now.
          .filter(
            (q: any) =>
              (isViewOnly && data.selectedConditions.length === 0) ||
              q.questionCondition?.length === 0 ||
              (data.selectedConditions &&
                data.selectedConditions.length > 0 &&
                q.questionCondition?.some(
                  (condition: string) =>
                    data.selectedConditions.findIndex(
                      (c: { questionId: string; flags: string[] }) =>
                        c.flags.indexOf(condition) !== -1
                    ) !== -1
                ))
          )
          .map((q: any, i: number) => (
            <div key={q.questionId} className="d-flex position-relative">
              <NeuAvatar
                className="mt-3 mr-3 "
                color="gray"
                size="default"
                initials={`${i + 1}`}
                style={{
                  '--avatar-background': 'var(--neu-gray-20)',
                  '--avatar-text': 'var(--neu-color-contrast)'
                }}
              />
              <Question
                data={data}
                q={q}
                setData={setData}
                viewOnly={isViewOnly}
                updateAggregatePart={updateAggregatePart}
              />
            </div>
          ))}
    </SurveyContainer>
  );
};

const mapReduxStateToProps = (state: any) => ({
  round: state.RoundReducer.currentRound,
  user: state.AuthorizedUser.authorizedUser
});

export default connect(mapReduxStateToProps)(Survey);
